<script setup lang="ts">
import { computed } from 'vue'

import {
  COMPARE_WINDOWS_BY_WINDOW,
  COMPARE_WINDOW_LABEL_KEYS,
  CONDITION_OPTIONS,
} from '../models/alert.constants'
import type {
  AlertMetricCondition,
  AlertWindowType,
  CompareWindow,
  ConditionOperator,
  ThresholdMode,
} from '../models/alert.types'
import { AlertMetricLabel } from '../models/reports-bridge'

import { useI18n } from '@/shared/i18n'
import { Badge, Input, Select } from '@/shared/ui'

const props = defineProps<{
  condition: AlertMetricCondition
  window: AlertWindowType | null
  customDays: number | null
  readOnly: boolean
  error?: boolean
  compareError?: boolean
  showOperator?: boolean
}>()
const emit = defineEmits<{
  update: [patch: Partial<AlertMetricCondition>]
}>()
const { t } = useI18n()

const operator = computed<ConditionOperator | null>({
  get: () => props.condition.operator,
  set: (value) => value && emit('update', { operator: value }),
})
const mode = computed<ThresholdMode | null>({
  get: () => props.condition.mode,
  set: (value) => value && emit('update', { mode: value }),
})
const compareWindow = computed<CompareWindow | null>({
  get: () => props.condition.compareWindow,
  set: (value) => emit('update', { compareWindow: value }),
})
const operatorOptions = computed(() =>
  CONDITION_OPTIONS.map((option) => ({
    value: option.operator,
    label: t(props.condition.mode === 'percentage' ? option.percentLabelKey : option.valueLabelKey),
  })),
)
const modeOptions = computed(() => [
  { value: 'value' as const, label: t('alerts.condition.mode.value') },
  { value: 'percentage' as const, label: t('alerts.condition.mode.percentage') },
])
const compareOptions = computed(() =>
  (props.window ? COMPARE_WINDOWS_BY_WINDOW[props.window] : []).map((value) => ({
    value,
    label: t(COMPARE_WINDOW_LABEL_KEYS[value], {
      days: value === 'previous_n_days' ? (props.customDays ?? 0) : 0,
    }),
  })),
)

function amount(value: string | number | undefined): void {
  if (value === '' || value === undefined) return emit('update', { amount: null })
  const numeric = Number(value)
  emit('update', { amount: Number.isFinite(numeric) ? numeric : null })
}
</script>

<template>
  <div
    class="condition-row flex flex-wrap items-center gap-sm w-full min-h-14 py-sm px-md border-0 rounded-md bg-surface-sunken"
  >
    <span v-if="showOperator !== false" class="condition-row__field inline-flex w-max">
      <Badge tone="info" shape="rounded">
        <AlertMetricLabel :column="condition.column" :size="14" />
      </Badge>
    </span>
    <span
      v-if="showOperator !== false"
      class="condition-row__divider w-[1px] h-8 bg-border"
      aria-hidden="true"
    />
    <span class="condition-row__field inline-flex w-max">
      <Select
        v-model="operator"
        :options="operatorOptions"
        :disabled="readOnly"
        size="sm"
        variant="picker"
      />
    </span>
    <span class="condition-row__divider w-[1px] h-8 bg-border" aria-hidden="true" />
    <span class="condition-row__field inline-flex w-max">
      <Select
        v-model="mode"
        :options="modeOptions"
        :disabled="readOnly"
        size="sm"
        variant="picker"
      />
    </span>
    <span class="condition-row__divider w-[1px] h-8 bg-border" aria-hidden="true" />
    <span class="condition-row__field inline-flex w-max">
      <Input
        :model-value="condition.amount ?? ''"
        type="number"
        size="sm"
        :label="t('alerts.condition.amount')"
        hide-label
        :placeholder="t('alerts.condition.amount')"
        :disabled="readOnly"
        :error="error"
        :error-message="t('alerts.validation.amount')"
        @update:model-value="amount"
      />
    </span>
    <template v-if="condition.mode === 'percentage'">
      <span class="condition-row__divider w-[1px] h-8 bg-border" aria-hidden="true" />
      <span class="condition-row__field condition-row__compare inline-flex w-max grid gap-2xs">
        <Select
          v-model="compareWindow"
          :options="compareOptions"
          :placeholder="t('alerts.condition.compare')"
          :disabled="readOnly || !window"
          :aria-invalid="compareError ? 'true' : undefined"
          size="sm"
          variant="picker"
        />
        <p
          v-if="compareError"
          class="condition-row__error m-0 text-danger text-[0.75rem]"
          role="alert"
        >
          {{ t('alerts.validation.compareWindow') }}
        </p>
      </span>
    </template>
  </div>
</template>
